import * as React from "react";
import { useState } from "react";
import { toast } from "sonner";
import { HelpCircle, Plus } from "lucide-react";
import { Button, Input, Popover, SimpleSelect, Tooltip } from "@/components/ui";
import { apiErrorMessage, FormRouteServices, RouteCode } from "@/services/bedrock";

interface Props {
  codes: RouteCode[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onCreated: (code: RouteCode) => void;
  canCreate: boolean;
}

export function RouteCodePicker({ codes, selectedId, onSelect, onCreated, canCreate }: Props) {
  const [open, setOpen] = useState(false);
  const [label, setLabel] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const close = () => {
    setOpen(false);
    setLabel("");
    setError(null);
  };

  const handleAdd = async () => {
    setSaving(true);
    setError(null);
    try {
      const code = await FormRouteServices.createCode(label.trim());
      onCreated(code);
      toast.success("Code added");
      close();
    } catch (e) {
      setError(apiErrorMessage(e, "We couldn't add that code. Try again."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-3" data-testid="route-code-picker">
        <span className="flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-slate-500">
          Route code
          <Tooltip>
            <Tooltip.Trigger
              render={
                <button
                  type="button"
                  aria-label="About route codes"
                  className="inline-flex text-slate-400 hover:text-slate-600 transition-colors cursor-help"
                >
                  <HelpCircle className="size-3.5" />
                </button>
              }
            />
            <Tooltip.Content className="max-w-xs whitespace-normal font-normal normal-case tracking-normal">
              Your organization's own code. It's part of every route you copy and tells SocialCoach the click is for
              you. No other organization has the same code.
            </Tooltip.Content>
          </Tooltip>
        </span>
        <div className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-sm">
          <SimpleSelect
            items={codes.map(code => ({ value: String(code.id), label: `${code.label} · ${code.code}` }))}
            value={selectedId ? String(selectedId) : undefined}
            onValueChange={value => value && onSelect(Number(value))}
            placeholder="Loading codes…"
          />
        </div>
        {canCreate && (
          <Popover open={open} onOpenChange={next => (next ? setOpen(true) : close())}>
            <Popover.Trigger
              render={
                <Button variant="outline" size="sm">
                  <Plus className="mr-1 h-3.5 w-3.5" />
                  New code
                </Button>
              }
            />
            <Popover.Content align="end" className="w-80 p-4 gap-3">
              <label className="flex flex-col gap-1.5 text-sm font-medium text-slate-700">
                Name
                <Input
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  placeholder="e.g. Second brand"
                  autoFocus
                />
              </label>
              {error && <p className="text-xs text-destructive">{error}</p>}
              <p className="text-xs text-slate-500 leading-relaxed">
                Most organizations only need Default. To tell emails and pages apart, give each route its own campaign
                name in step 4. Add a code only when you want a separate one, such as for a second brand. We create the
                code for you, so it's always unique to your organization.
              </p>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={close}>
                  Cancel
                </Button>
                <Button size="sm" onClick={handleAdd} loading={saving} disabled={!label.trim()}>
                  Add code
                </Button>
              </div>
            </Popover.Content>
          </Popover>
        )}
      </div>
    </div>
  );
}

/** "Default" first, then the order the codes were added. */
export function sortRouteCodes(codes: RouteCode[]): RouteCode[] {
  return [...codes].sort((a, b) => {
    if (a.label === "Default" && b.label !== "Default") return -1;
    if (b.label === "Default" && a.label !== "Default") return 1;
    return a.createdAt.localeCompare(b.createdAt);
  });
}
